import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { RHFAutocomplete, RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { Hidden, InputAdornment, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import UniqueTextField from 'src/components/unique-text-field/UniqueTextField';

export default function EmpBankNewEditForm({ bankNmData, bankTypeData, onDuplicateStatusChange, currentEmp }) {
  const { control, setValue, watch, resetField, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'empBank',
  });

  // Automatically append a blank bank object if empBank is initially empty
  useEffect(() => {
    if (fields.length === 0) {
      append({
        bankSrno: 0,
        bankNm: null,
        branch: '',
        accNm: '',
        accNo: '',
        accTyp: null,
        ifsc: '',
        swiftCd: '',
        startDate: null,
        endDate: null,
      });
    }
  }, [append, fields.length]);

  const handleAdd = () => {
    append({
      bankSrno: 0,
      bankNm: null,
      branch: '',
      accNm: '',
      accNo: '',
      accTyp: null,
      ifsc: '',
      swiftCd: '',
      startDate: null,
      endDate: null,
    });
  };

  const handleRemove = (index) => {
    const itemId = fields[index]?.id;
    if (itemId && onDuplicateStatusChange) {
      onDuplicateStatusChange(`bank-${itemId}`)(false);
    }
    remove(index);
  };

  return (
    <>
      <Box
        mt={4}
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
        mb={3}
      >
        <Box>
          <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
            Bank Details:
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-end', md: 'center' }}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0, minWidth: '140px', justifyContent: 'flex-start' }}
          >
            Add Bank
          </Button>
        </Stack>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />} spacing={2}>
        {fields.map((item, index) => {
          const startDateValue = watch(`empBank[${index}].startDate`);
          const endDateValue = watch(`empBank[${index}].endDate`);

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const maxStartDate = (() => {
            if (endDateValue) {
              const end = new Date(endDateValue);
              if (!Number.isNaN(end.getTime())) {
                end.setHours(0, 0, 0, 0);
                const dayBeforeEnd = new Date(end);
                dayBeforeEnd.setDate(dayBeforeEnd.getDate() - 1);
                return dayBeforeEnd < today ? dayBeforeEnd : today;
              }
            }
            return today;
          })();

          const minEndDate = (() => {
            if (startDateValue) {
              const start = new Date(startDateValue);
              if (!Number.isNaN(start.getTime())) {
                start.setHours(0, 0, 0, 0);
                const dayAfterStart = new Date(start);
                dayAfterStart.setDate(dayAfterStart.getDate() + 1);
                return dayAfterStart > today ? dayAfterStart : today;
              }
            }
            return today;
          })();

          return (
            <Stack key={item.id} spacing={1.5}>
            <Typography variant="subtitle2" sx={{ color: 'primary', mb: 1 }}>
              Bank {index + 1}:
            </Typography>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <RHFTextField name={`empBank[${index}].bankSrno`} />
            </Box>
            <Box
              rowGap={2}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empBank[${index}].bankNm`}
                  placeholder="Select bank name"
                  label={
                    <div>
                      Bank Name<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={bankNmData}
                  showPlusIcon
                  getOptionLabel={(option) => option.masterName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.masterId}>
                      {option.masterName}
                    </li>
                  )}
                />
              </Stack>
              <RHFTextField
                name={`empBank[${index}].branch`}
                label={
                  <div>
                    Bank Branch<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                  </div>
                }
                placeholder="Bank Branch"
              />
              <RHFTextField
                name={`empBank[${index}].accNm`}
                label={
                  <div>
                    Account Holder Name
                    <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                  </div>
                }
                placeholder="Account Holder Name"
              />
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empBank[${index}].accTyp`}
                  placeholder="Select Bank account Type"
                  label={
                    <div>
                      Bank Account Type
                      <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={bankTypeData}
                  showPlusIcon
                  getOptionLabel={(option) => option.masterName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.masterId}>
                      {option.masterName}
                    </li>
                  )}
                />
              </Stack>
              <Controller
                name={`empBank[${index}].accNo`}
                control={control}
                rules={{ required: 'Account Number is required' }}
                render={({ field, fieldState }) => (
                  <UniqueTextField
                    field={field}
                    error={fieldState.error}
                    maskType="ACCOUNT"
                    label={
                      <div>
                        Account Number{' '}
                        <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                      </div>
                    }
                    placeholder="Account Number"
                    fieldName="accountNumber"
                    apiEndpoint="/rest/employee/external/check-duplicate-account-number"
                    entityId={item.bankSrno > 0 ? item.bankSrno : null}
                    onDuplicateStatusChange={onDuplicateStatusChange ? onDuplicateStatusChange(`bank-${item.id}`) : undefined}
                    duplicateMessage="This Account Number already exists"
                    validationRegex={/^[0-9]{9,18}$/}
                    sx={{ mb: 1 }}
                  />
                )}
              />

              <RHFTextField
                name={`empBank[${index}].ifsc`}
                label={
                  <div>
                    IFSC<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                  </div>
                }
                placeholder="IFSC"
                sx={{ mb: 1 }}
              />
              <RHFTextField
                name={`empBank[${index}].swiftCd`}
                label="Swift Code"
                placeholder="Swift Code"
                sx={{ mb: 1 }}
              />
              <Controller
                name={`empBank[${index}].startDate`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label={
                      <div>
                        Start Date<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                      </div>
                    }
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger(`empBank[${index}].startDate`);
                      trigger(`empBank[${index}].endDate`);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                    maxDate={maxStartDate}
                  />
                )}
              />
              <Controller
                name={`empBank[${index}].endDate`}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label={<div>End Date</div>}
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger(`empBank[${index}].endDate`);
                      trigger(`empBank[${index}].startDate`);
                    }}
                    minDate={minEndDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-end', md: 'center' }}
              justifyContent="flex-end"
            >
              {fields.length > 1 && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                  onClick={() => handleRemove(index)}
                >
                  Remove
                </Button>
              )}
            </Stack>
          </Stack>
          );
        })}
      </Stack>
    </>
  );
}
EmpBankNewEditForm.propTypes = {
  bankNmData: PropTypes.array,
  bankTypeData: PropTypes.array,
  onDuplicateStatusChange: PropTypes.func,
  currentEmp: PropTypes.object,
};
